import { useCallback, useSyncExternalStore } from 'react';

/**
 * A CSS media query as React state.
 *
 * Used where a layout difference is a behaviour difference and not just a
 * class name — the session page mounts the chat panel beside the video on a
 * wide screen and inside a drawer below it, which is one component in two
 * places, not one component styled two ways.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );

  const getSnapshot = useCallback(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  }, [query]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/** The session page's split point: chat beside the video from here up. */
export const WIDE_SCREEN_QUERY = '(min-width: 1024px)';
